import { useEffect } from 'react'
import { ThemeContext } from './theme-context.ts'
import type { Theme } from './types.ts'
import { useThemeStore } from './use-theme.ts'

/**
 * Root theme provider. Reads the persisted theme from the global store,
 * syncs it to `document.documentElement[data-bs-theme]`, and exposes it to
 * descendants via `ThemeContext`. Pass `initialTheme` to seed the store on
 * first mount (useful for tests and stories that need a specific starting
 * theme). Use at most once at the app root.
 */
export const WithTheme = ({ initialTheme, children }: { initialTheme?: Theme; children: React.ReactNode }) => {
  const { theme, setTheme } = useThemeStore()

  useEffect(() => {
    if (initialTheme) {
      setTheme(initialTheme)
    }
  }, [initialTheme, setTheme])

  useEffect(() => {
    const syncTheme = (state: { theme: Theme }) => {
      // eslint-disable-next-line no-restricted-properties -- Theme provider must set root element attribute
      document.documentElement.setAttribute('data-bs-theme', state.theme)
    }
    syncTheme(useThemeStore.getState())
    return useThemeStore.subscribe(syncTheme)
  }, [])

  return <ThemeContext.Provider value={theme}>{children}</ThemeContext.Provider>
}

type ScopedThemeProps = {
  theme: Theme
  className?: string
  children: React.ReactNode
}

/**
 * Scoped theme provider. Renders a wrapper `<div>` that carries both sides
 * of the theme system for its subtree: `ThemeContext` for React consumers
 * (`useTheme`) and a `data-bs-theme` attribute that activates the matching
 * CSS token layer (see `createGlobalTheme('[data-bs-theme="…"]', …)` in
 * `tokens/theme-contract.css.ts`). It does not mutate the global store,
 * localStorage, or the document root, so sibling subtrees can render under
 * different themes (e.g. Storybook sticker sheets showing light and dark
 * variants side-by-side). Pass `className` to style the wrapper.
 */
export const ScopedTheme = ({ theme, className, children }: ScopedThemeProps) => (
  <ThemeContext.Provider value={theme}>
    <div className={className} data-bs-theme={theme}>
      {children}
    </div>
  </ThemeContext.Provider>
)
